import React, { useEffect, useState } from 'react';

/**
 * SplashScreen
 * Cold-boot splash: square "S" icon scales/pulses → cross-fades into the
 * full Simplly wordmark → wipes away. Runs once per browser session.
 *
 * Pure CSS animation — no Lottie / no extra deps.
 */
export default function SplashScreen() {
  const [visible, setVisible] = useState(() => {
    try {
      return sessionStorage.getItem('simplly_splash_seen') !== '1';
    } catch {
      return true;
    }
  });
  const [phase, setPhase] = useState('icon'); // 'icon' | 'word' | 'leave'

  useEffect(() => {
    if (!visible) return;
    const t1 = setTimeout(() => setPhase('word'), 850);   // icon → wordmark
    const t2 = setTimeout(() => setPhase('leave'), 1700); // begin exit wipe
    const t3 = setTimeout(() => {
      try { sessionStorage.setItem('simplly_splash_seen', '1'); } catch {}
      setVisible(false);
    }, 2300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="simplly-splash"
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FAFAFA',
        pointerEvents: phase === 'leave' ? 'none' : 'auto',
        opacity: phase === 'leave' ? 0 : 1,
        transition: 'opacity 600ms ease',
      }}
    >
      {/* Subtle teal + pink ambient glow */}
      <div
        style={{
          position: 'absolute',
          width: 520,
          height: 520,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 35% 35%, rgba(0,163,161,0.18), transparent 60%), radial-gradient(circle at 70% 70%, rgba(233,78,137,0.15), transparent 60%)',
          filter: 'blur(60px)',
        }}
      />

      <div
        style={{
          position: 'relative',
          width: 220,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* S-icon — visible during 'icon' phase, then fades */}
        <img
          src="/simplly_favicon.png"
          alt=""
          style={{
            position: 'absolute',
            height: 84,
            width: 84,
            objectFit: 'contain',
            opacity: phase === 'icon' ? 1 : 0,
            transform:
              phase === 'icon'
                ? 'scale(1)'
                : 'scale(0.55) translateX(-92px)',
            transition:
              'transform 750ms cubic-bezier(.65,.05,.36,1), opacity 350ms ease',
            animation:
              phase === 'icon'
                ? 'simplly-pulse 850ms ease-out 1 both'
                : 'none',
            filter: 'drop-shadow(0 6px 20px rgba(0,163,161,0.25))',
          }}
        />
        {/* Wordmark — fades & slides in for 'word' phase */}
        <img
          src="/simplly_logo.png"
          alt="Simplly"
          style={{
            height: 56,
            objectFit: 'contain',
            opacity: phase === 'icon' ? 0 : 1,
            transform: phase === 'icon' ? 'translateY(8px)' : 'translateY(0)',
            transition:
              'opacity 500ms ease 80ms, transform 600ms cubic-bezier(.2,.8,.2,1) 80ms',
          }}
        />
      </div>

      <style>{`
        @keyframes simplly-pulse {
          0%   { transform: scale(0.7); opacity: 0; }
          50%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
